<template>
  <div style="padding:1em 2em 64px 2em">
    <template v-for="(slide, index) in slides">
      <slide-base
        :key="'slide-' + index"
        :section="section"
        :slide-num="slideNum"
        :primary="slide.primary"
        :secondary="slide.secondary"
        :visible="slide.visible"
        @slideLoaded="slideLoaded"
        @activeFinish="activeFinish()"
        @activeWaiting="({ types }) => updateActive(types)"
        @out="loadNextSlide()"
        ></slide-base>
    </template>
    <transition
      name="slideUp"
      v-bind:css="false"
      v-on:before-enter="beforeEnterFooter"
      v-on:enter="enterFooter"
      v-on:leave="leaveFooter">
      <v-footer v-if="isLoaded" dark fixed padless>
        <v-toolbar flat style="background:none">
          <v-btn light @click="back()" :disabled="slideNum === 1">
            <v-icon left>fal fa-chevron-left</v-icon>
            <span v-if="$vuetify.breakpoint.smAndUp">Back</span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn light @click="backToHome()">
            <v-icon left>fal fa-home</v-icon>
            <span v-if="$vuetify.breakpoint.smAndUp">Home</span>
          </v-btn>
          <v-menu top offset-y>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" light>
                <v-icon left>fal fa-presentation</v-icon>
                <span v-if="$vuetify.breakpoint.smAndUp" style="margin-right:6px">Slide</span> {{ slideNum }}/{{ slides.length }}
              </v-btn>
            </template>
            <v-list dense>
              <v-subheader>Section: {{ sectionName }}</v-subheader>
              <v-divider></v-divider>
              <template v-for="i in highSlide">
                <v-list-item v-if="i !== slideNum" :key="'slide-' + i" @click="jumpToSlide(i)">
                  <v-list-item-title>{{ slides[i-1] && 'name' in slides[i-1] ? slides[i-1].name + ' (Slide ' + i + ')' : 'Slide ' + i }}</v-list-item-title>
                </v-list-item>
              </template>
              <v-list-item v-if="highSlide === 1">
                <v-list-item-subtitle>No slides to jump to</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-spacer></v-spacer>
          <v-dialog v-if="pendingActiveContent" v-model="waitingDialog" width="350">
            <template v-slot:activator="{ on:dialogOn }">
              <v-tooltip top>
                <template v-slot:activator="{ on:tooltipOn }">
                  <v-btn v-on="{ ...dialogOn, ...tooltipOn }" light>
                    Waiting...
                  </v-btn>
                </template>
                <span>You must {{ pendingText }} before continuing.</span>
              </v-tooltip>
            </template>
            <v-card>
              <v-card-title>Waiting on Interactive Content</v-card-title>
              <v-card-text>You must {{ pendingText }} before you can progress to the next slide. {{ pendingText.search('video') >= 0 ? 'Please ensure that you watch the video(s) all the way to the very end (the video will automatically hide when you finish it).' : '' }}</v-card-text>
              <v-card-actions>
                <v-btn text @click="waitingDialog = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-btn v-else :disabled="!scrolledToBottom" light @click="next()">
            <span v-if="$vuetify.breakpoint.smAndUp">{{ slides.length > slideNum ? 'Next' : 'Finish' }}</span>
            <v-icon right>fal fa-chevron-right</v-icon>
          </v-btn>
        </v-toolbar>
      </v-footer>
    </transition>
  </div>
</template>
<script>
import { ref, computed, watch, onMounted } from '@vue/composition-api'
import { hasRole } from '../helpers/has'
import Velocity from 'velocity-animate'
export default {
  components: {
    slideBase: () => import('@/components/slides/_base')
  },
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user)
    const section = computed(() => root.$route.params.section)
    const slideNum = computed(() => parseInt(root.$route.params.slide) || 1)
    const activeSlide = ref(-1)
    const trackerSections = computed(() => root.$store.state.tracker.sections)
    const sectionName = ref('')

    const isLoaded = ref(false)
    const slides = ref([])

    const highSlide = ref(1)

    const goToSlide = ref(null)

    watch(section, async () => {
      let term = root.$store.state.term
      if (term === '') {
        let data = await root.$store.dispatch('loadChecklist')
        if (data === false) {
          if (hasRole(user.value, 'Employee')) {
            root.$router.replace('/admin/preview/' + section + '/' + slideNum)
          } else {
            alert('The system does not have a record for your starting term. Please contact Technology Services as techsupport@covenant.edu and tell us about this problem and we can work to fix it.')
          }
          return
        }
        term = root.$store.state.term
      }
      root.$feathers.service('orientation/section').find({ query: { term, code: section.value, active: true } }).then(({ data }) => {
        if (data.length === 0) {
          root.$router.replace('/')
          return
        }
        sectionName.value = data[0].label
        for (let i = 0; i < data[0].slides.length; i++) {
          if (data[0].slides[i].name === 'Highland Academic Program') {
            // Hide the Highland Academic Program slide from anyone who is not a provisional or limited load student
            if (!['PV', 'LL'].includes(root.$store.state.academics.admitCode)) {
              continue
            }
          }
          slides.value.push({ ...data[0].slides[i], visible: false })
        }
        for (const { section: code, highSlide: trackerHigh, lastSlide } of trackerSections.value) {
          if (code === section.value) {
            if (trackerHigh && trackerHigh > 0) highSlide.value = trackerHigh
            else highSlide.value = 1
            if (lastSlide && lastSlide > 0) goToSlide.value = lastSlide
            else goToSlide.value = 1
            loadNextSlide()
            return
          }
        }
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'The section you attempted to access is currently locked. Please use the list below to access an unlocked section of your orientation.' })
        root.$router.replace('/')
      })
    })

    watch(slideNum, () => {
      if (slideNum.value !== goToSlide.value) {
        hideCurrentSlide()
        goToSlide.value = slideNum.value
      }
    })

    onMounted(() => {
      isLoaded.value = true
    })

    function beforeEnterFooter (el) {
      el.style.opacity = 0
      el.style.bottom = '-100px'
    }
    function enterFooter (el, done) {
      Velocity(
        el,
        { opacity: 0.9, bottom: '0px' },
        { complete: done }
      )
    }
    function leaveFooter (el, done) {
      Velocity(
        el,
        { opacity: 0.01, bottom: '-100px' },
        {
          complete: () => {
            done()
            root.$router.push('/')
          }
        }
      )
    }

    function backToHome () {
      hideCurrentSlide()
      isLoaded.value = false
      goToSlide.value = null
    }

    function hideCurrentSlide () {
      let index = activeSlide.value - 1
      // Sometimes this gets called on initial load so we need to make sure the slides exist before trying to hide them
      if (index in slides.value) {
        let temp = JSON.parse(JSON.stringify(slides.value[index]))
        temp.visible = false
        slides.value.splice(index, 1, temp)
      }
    }

    function back () {
      goToSlide.value = slideNum.value - 1
      hideCurrentSlide()
    }

    function next () {
      goToSlide.value = slideNum.value + 1
      hideCurrentSlide()
    }

    function jumpToSlide (num) {
      root.$router.push('/' + section.value + '/' + num)
    }

    async function loadNextSlide () {
      if (goToSlide.value == null) {
        return
      }
      if (goToSlide.value > 0) {
        // Track the slide progress
        if (goToSlide.value > highSlide.value) highSlide.value = goToSlide.value
        let duration = null
        if (slideStartTime.value != null) {
          let end = new Date()
          // Calculate how long the student was on the slide
          duration = (end - slideStartTime.value) / 1000
        }
        // send to the slide tracker
        await root.$store.dispatch('slideTracker', { section: section.value, slideNum: goToSlide.value, duration, sectionCompleted: goToSlide.value > slides.value.length })
        // Load the next slide
        if (goToSlide.value <= slides.value.length) {
          let url = '/' + section.value + '/' + (goToSlide.value)
          if (slideNum.value === goToSlide.value) {
            if (root.$route.path !== url) root.$router.replace(url)
          } else if (root.$route.path !== url) {
            root.$router.push(url)
          }
          let index = goToSlide.value - 1
          let temp = JSON.parse(JSON.stringify(slides.value[index]))
          pendingText.value = ''
          temp.visible = true
          slides.value.splice(index, 1, temp)
          activeSlide.value = goToSlide.value
        } else {
          // root.$store.dispatch('completeSection', { section: section.value })
          backToHome()
        }
      } else {
        backToHome()
      }
    }

    function activeFinish () {
      pendingActiveContent.value = false
      pendingText.value = ''
    }

    function updateActive (types) {
      let pendingTypes = []
      if (types.length === 0) {
        pendingActiveContent.value = false
        pendingText.value = ''
        return
      }
      for (const type of types) {
        let obj = {
          search: '',
          text: '',
          plural: ''
        }
        switch (type) {
          case 'Agreement':
            obj = { search: 'agreement', text: 'confirm the agreement', plural: 'agreements' }
            break
          case 'QuizCard':
            obj = { search: 'quiz', text: 'complete the quiz', plural: 'quizzes' }
            break
          case 'VideoCard':
            obj = { search: 'video', text: 'completely watch the video', plural: 'videos' }
            break
          case 'Carousel':
            obj = { search: 'the entire carousel', text: 'scroll completely through the entire carousel', plural: 'all of the carousels' }
            break
          case 'Popups':
            obj = { search: 'popup set', text: 'click on and review each item in the popup set', plural: 'popup sets' }
            break
          case 'Timeline':
            obj = { search: 'the timeline', text: 'click on and review each item in the timeline', plural: 'each timeline' }
            break
        }
        if (obj.search === '') continue
        if (pendingText.value.search(obj.search) < 0) {
          pendingTypes.push(obj.text)
          pendingText.value = pendingTypes.join(', ')
          if (pendingText.value.search(', ') > 0) {
            pendingText.value = pendingText.value.substr(0, pendingText.value.lastIndexOf(', ') + 1) + ' and' + pendingText.value.substr(pendingText.value.lastIndexOf(', ') + 1)
          }
        } else if (pendingText.value.search(obj.plural) < 0) {
          pendingText.value = pendingText.value.replace(new RegExp(obj.search, 'g'), obj.plural)
        }
        pendingActiveContent.value = true
      }
    }

    const pendingActiveContent = ref(false)
    const waitingDialog = ref(false)
    const pendingText = ref('')

    const slideStartTime = ref(null)

    function handleScroll () {
      if (document.documentElement.scrollHeight - window.innerHeight - window.scrollY < 50) {
        scrolledToBottom.value = true
        window.removeEventListener('scroll', handleScroll)
      }
    }

    const scrolledToBottom = ref(false)
    function slideLoaded () {
      slideStartTime.value = new Date()
      scrolledToBottom.value = false
      if (window.innerHeight > document.getElementsByClassName('slide-base')[slideNum.value - 1].clientHeight) scrolledToBottom.value = true
      window.addEventListener('scroll', handleScroll)
    }

    return {
      section,
      slideNum,
      activeSlide,
      sectionName,
      isLoaded,
      slides,
      highSlide,
      beforeEnterFooter,
      enterFooter,
      leaveFooter,
      backToHome,
      goToSlide,
      back,
      next,
      jumpToSlide,
      loadNextSlide,
      activeFinish,
      updateActive,
      pendingActiveContent,
      waitingDialog,
      pendingText,
      slideStartTime,
      scrolledToBottom,
      slideLoaded
    }
  }
}
</script>
